// FacilityWorkflow.jsx — facility-specific badge renderers and RBAC/status
// data for facility-backed tabs (Facility Core Registry, Feed Production).
// The action dropdown itself, its Reject/Delete confirmation modals, and the
// busy overlay now live in component/shared/RowActionMenu.jsx (the single
// canonical action-dropdown used by every Core Module); this file only keeps
// what's specific to facility rows (badges, RBAC matrix, status labels).

//  Badge renderers (DataTables HTML strings)
const FUNC_META = {
  active:               { dot: '#22c55e', text: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'Active' },
  under_maintenance:    { dot: '#f59e0b', text: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Under Maintenance' },
  inactive:             { dot: '#ef4444', text: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Inactive' },
  under_rehabilitation: { dot: '#3b82f6', text: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'Under Rehabilitation' },
  decommissioned:       { dot: '#9ca3af', text: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb', label: 'Decommissioned' },
};

// Legacy vocabulary (pre v2.0 field-dictionary fix) — kept so facilities
// saved under the old ENUM still resolve to a badge instead of "Unknown".
const FUNC_META_ALIASES = {
  fully_functional: 'active',
  functional: 'active',
  partially_functional: 'under_maintenance',
  non_functional: 'inactive',
  under_construction: 'under_rehabilitation',
};

function getFunctionalStatusMeta(status) {
  const key = String(status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const resolved = FUNC_META[key] ? key : FUNC_META_ALIASES[key];
  return FUNC_META[resolved] ?? { dot: '#9ca3af', text: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb', label: String(status ?? '').trim() || 'Unknown' };
}

export function functionalBadgeHtml(status, type) {
  if (type !== 'display') return status ?? '';
  if (!status) return '';
  const meta = getFunctionalStatusMeta(status);
  return `<span style="display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:${meta.text};background:${meta.bg};border:1px solid ${meta.border};padding:4px 10px;border-radius:999px;white-space:nowrap;">` +
    `<span style="width:8px;height:8px;border-radius:50%;background:${meta.dot};flex-shrink:0;display:inline-block"></span>${meta.label}</span>`;
}

const PRIORITY_CLR = { High: '#dc2626', Medium: '#d97706', Low: '#16a34a', None: '#9ca3af' };
export function priorityBadgeHtml(priority, type) {
  if (type !== 'display') return priority ?? '';
  const color = PRIORITY_CLR[priority] || '#6b7280';
  return `<span style="font-size:12px;font-weight:600;color:${color}">${priority || '-'}</span>`;
}

const LIC_CLR = { Licensed: '#15803d', Unlicensed: '#dc2626', Pending: '#b45309', Exempt: '#6b7280' };
export function licensedBadgeHtml(status, type) {
  if (type !== 'display') return status ?? '';
  if (!status) return '';
  const color = LIC_CLR[status] || '#6b7280';
  return `<span style="font-size:12px;font-weight:600;color:${color}">${status}</span>`;
}

const DS_BG   = { Draft: '#f3f4f6', Submitted: '#eff6ff', Validated: '#f5f3ff', Approved: '#f0fdf4', Rejected: '#fef2f2' };
const DS_TEXT = { Draft: '#6b7280', Submitted: '#1d4ed8', Validated: '#7c3aed', Approved: '#15803d', Rejected: '#dc2626' };
export function dataStatusBadgeHtml(status, type) {
  if (type !== 'display') return status ?? '';
  if (!status) return '';
  const bg   = DS_BG[status]   || '#f3f4f6';
  const text = DS_TEXT[status] || '#6b7280';
  return `<span style="font-size:11px;font-weight:600;color:${text};background:${bg};padding:2px 8px;border-radius:999px;white-space:nowrap">${status}</span>`;
}

export function coordinateCellHtml(value, type) {
  if (type !== 'display') return value ?? '';
  if (!value) return '-';
  return `<span style="font-family:ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;font-size:12px;font-weight:600;color:#374151;white-space:nowrap;">${String(value)}</span>`;
}

export const ACT = (_d, type) =>
  type !== 'display' ? '' :
  `<button class="nlims-action-btn" data-action="menu" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:4px 14px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;" title="Actions">&#8943;</button>`;

//  Role capability matrix  shared across every facility-backed tab (DCIC-06 workflow)
export const ROLE_CAN = {
  R01: { edit: true,  submit: true,  validate: true,  approve: true,  reject: true,  delete: true  },
  R02: { edit: false, submit: false, validate: false, approve: true,  reject: true,  delete: false },
  R03: { edit: false, submit: false, validate: true,  approve: false, reject: true,  delete: false },
  R04: { edit: true,  submit: true,  validate: false, approve: false, reject: false, delete: false },
  R05: { edit: false, submit: false, validate: false, approve: false, reject: false, delete: false },
};

export const WORKFLOW_STATUS_BY_ACTION = {
  submit: 'submitted',
  validate: 'validated',
  approve: 'approved',
  reject: 'rejected',
};

export const ROLE_WORKFLOW_TRANSITIONS = {
  R01: {
    draft: ['submitted'],
    submitted: ['validated', 'approved', 'rejected'],
    validated: ['approved', 'rejected'],
    approved: ['rejected'],
    rejected: ['submitted'],
  },
  R02: {
    validated: ['approved', 'rejected'],
  },
  R03: {
    submitted: ['validated', 'rejected'],
  },
  R04: {
    draft: ['submitted'],
    rejected: ['submitted'],
  },
  R05: {},
};

const WORKFLOW_STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  validated: 'Validated',
  approved: 'Approved',
  rejected: 'Rejected',
};

export function humanizeWorkflowStatus(value) {
  const text = String(value ?? '').trim();
  if (!text) return 'Draft';
  return text.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getWorkflowStatusLabel(value, options = []) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return 'Draft';
  const found = options.find((o) => String(typeof o === 'string' ? o : o.value ?? '').trim().toLowerCase() === normalized);
  if (found) return typeof found === 'string' ? found : (found.label ?? humanizeWorkflowStatus(found.value));
  return WORKFLOW_STATUS_LABELS[normalized] ?? humanizeWorkflowStatus(normalized);
}

export function getWorkflowStatusOptions(currentStatus, roleId, options = []) {
  const current = String(currentStatus ?? 'draft').trim().toLowerCase() || 'draft';
  const next = ROLE_WORKFLOW_TRANSITIONS[roleId]?.[current] ?? [];
  const ordered = [current, ...next];
  const seen = new Set();
  return ordered
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    })
    .map((value) => ({
      value,
      label: getWorkflowStatusLabel(value, options),
    }));
}

// R01 may edit at any status; R04 only while Draft or Rejected. Matches the
// `<ActionDropdown editableStatuses>` shape expected by
// component/shared/RowActionMenu.jsx's computeMenuFlags().
export const EDITABLE_STATUSES = {
  R01: ['draft', 'submitted', 'validated', 'approved', 'rejected'],
  R04: ['draft', 'rejected'],
};
